<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'
	import type {
		Store,
		SearchField,
		StaticSearchField,
		StaticSearchFieldKey,
		VehicleStaticSearchFieldValue,
		InventoryQuestionValue,
		ParsedStateParameters,
	} from 'utility/search-fields'
	import type { ActivityInput } from '@isoftdata/svelte-sidebar'
	import type { i18n } from 'types/common'

	import Icon from '@isoftdata/svelte-icon'
	import Button from '@isoftdata/svelte-button'
	import DateRange from 'components/search/DateRange.svelte'
	import UserAccountsAutocomplete from './UserAccountAutocomplete.svelte'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import InputSearchField from 'components/search/InputSearchField.svelte'
	import StoreSelect from 'components/search/StoreSelect.svelte'
	import StatusSelect from 'components/search/StatusSelect.svelte'
	import YearRangeInput from 'components/search/YearRangeInput.svelte'
	import CategoryAutocomplete from 'components/search/SearchFieldCategory.svelte'
	import UserStatusAutocomplete from 'components/search/UserStatusAutocomplete.svelte'
	import InventoryQuestionField from 'components/search/InventoryQuestionField.svelte'
	import ModelAutocomplete from 'components/search/SearchFieldModel.svelte'
	import InventoryTypeAutocomplete from 'components/search/SearchFieldInventoryType.svelte'
	import ManufacturerAutocomplete from 'components/search/SearchFieldManufacturer.svelte'
	import AddEditSearchFilters from 'components/search/AddEditSearchFilters.svelte'
	import TitleTypeAutocomplete from './TitleTypeAutocomplete.svelte'
	import TitleStatusAutocomplete from './TitleStatusAutocomplete.svelte'
	import LocationAutocomplete from 'components/search/LocationAutocomplete.svelte'
	import DismantledCheckbox from './DismantledCheckbox.svelte'
	import CurrencyRange from 'components/search/CurrencyRange.svelte'
	import NullableRadioCheckbox from 'components/NullableRadioCheckbox.svelte'
	import {
		loadInventoryQuestions,
		getStaticSearchFieldsWithDependencyGraph,
		getInitialSelectedSearchFields,
		getSearchFieldsByCategory,
		getValidTypeFields,
		saveUserSetting,
		computeSearchParameterValues,
		getParsedTypeFields,
		getParsedQAFields,
	} from 'utility/search-fields'
	import Pin from 'components/search/Pin.svelte'
	import { SvelteSet } from 'svelte/reactivity'
	import { getContext, onMount } from 'svelte'
	import { klona } from 'klona'
	import { PersistedUserState } from '@isoftdata/svelte-store-user-local-writable'
	import session from 'stores/session'

	interface Props {
		parsedParameters: ParsedStateParameters
		authorizedStores?: Array<Store>
		stores?: Array<number>
		asr: SvelteAsr
		userPinnedSearchFields?: Array<string>
	}

	let { parsedParameters, authorizedStores = [], stores = [], asr, userPinnedSearchFields = [] }: Props = $props()

	let inEditMode: boolean = $state(false)
	let inventoryQuestions: Array<SearchField> = $state([])
	/** Track if the user has run a search, so we can replace the search activity if they do another */
	let hasSearched = $state(false)
	let staticSearchFieldValues: VehicleStaticSearchFieldValue = $state({
		...klona(getDefaultStaticSearchFieldValues()),
		...getStaticSearchFieldsWithParsedParams(),
	})
	let typeFieldValues: Record<string, string> = $state({}) //Type fields can only be strings()

	const { t: translate } = getContext<i18n>('i18next')
	const mediator = getContext<Mediator>('mediator')

	const lastVehicleSearch = new PersistedUserState<Record<string, unknown>>(
		$session.userAccountId,
		'lastVehicleSearch',
		{},
	)
	/** List of all static search fields, with their dependencies. (All possible search fields) */
	const staticSearchFields = getStaticSearchFieldsWithDependencyGraph('VEHICLE')
	const staticSearchFieldsMap: Map<StaticSearchFieldKey, StaticSearchField> = new Map(
		staticSearchFields.map(field => [field.key, field]),
	)
	const pinnedSearchFields: SvelteSet<string> = new SvelteSet()

	const vehicleStaticSearchFields = [
		'STORE',
		'STOCK_NUMBER',
		'VIN',
		'INVENTORY_TYPE',
		'STATUS',
		'USER_STATUS',
		'YEAR_RANGE',
		'VEHICLE_MANUFACTURER',
		'VEHICLE_MODEL',
		'CATEGORY',
		'ADJUSTER',
		'BODY_STYLE',
		'CLAIM_NUMBER',
		'LOCATION',
		'JUNKING_NUMBER',
		'FILE_NUMBER',
		'EXTERNAL_COLOR',
		'ENTERED_BY_USER',
		'DISMANTLER',
		'DISMANTLED',
		'DESCRIPTION',
		'DATE_ENTERED',
		'PICKUP_LOCATION',
		'PURCHASED_FROM',
		'RETAIL_PRICE',
		'SELLER_CONTACT',
		'SELLER_INFO',
		'TITLE_STATUS',
		'TITLE_NUMBER',
		'TITLE_TYPE',
	]

	function getDefaultStaticSearchFieldValues() {
		return Object.freeze({
			VIN: '',
			STATUS: '@',
			CATEGORY: null,
			ADJUSTER: null,
			LOCATION: null,
			CONDITION: null,
			STOCK_NUMBER: '',
			BODY_STYLE: null,
			DISMANTLER: null,
			DISMANTLED: null,
			TITLE_TYPE: null,
			USER_STATUS: null,
			FILE_NUMBER: null,
			DESCRIPTION: null,
			SELLER_INFO: null,
			CLAIM_NUMBER: null,
			DATE_ENTERED: { from: '', to: '' },
			RETAIL_PRICE: { from: '', to: '' },
			TITLE_STATUS: null,
			TITLE_NUMBER: null,
			VEHICLE_MODEL: null,
			INVENTORY_TYPE: null,
			JUNKING_NUMBER: null,
			EXTERNAL_COLOR: null,
			PURCHASED_FROM: null,
			SELLER_CONTACT: null,
			ENTERED_BY_USER: null,
			PICKUP_LOCATION: null,
			VEHICLE_MANUFACTURER: null,
			YEAR_RANGE: { from: null, to: null },
			HAS_PUBLIC_IMAGES: null,
			STORE: stores, //default to their current store, passed from part-search.ts
		})
	}

	function getStaticSearchFieldsWithParsedParams() {
		return {
			STATUS: parsedParameters.STATUS ?? '@',
			TITLE_TYPE: parsedParameters.TITLE_TYPE ?? null,
			TITLE_STATUS: parsedParameters.TITLE_STATUS ?? null,
			DISMANTLED: parsedParameters.DISMANTLED ?? null,
			YEAR_RANGE: parsedParameters.YEAR_RANGE ?? { from: null, to: null },
			DATE_ENTERED: parsedParameters.DATE_ENTERED ?? { from: '', to: '' },
			RETAIL_PRICE: parsedParameters.RETAIL_PRICE ?? { from: '', to: '' },
			HAS_PUBLIC_IMAGES: parsedParameters.HAS_PUBLIC_IMAGES ?? null,
			STOCK_NUMBER: parsedParameters.STOCK_NUMBER ?? '',
			VIN: parsedParameters.VIN ?? '',
			ADJUSTER: parsedParameters.ADJUSTER ?? null,
			BODY_STYLE: parsedParameters.BODY_STYLE ?? null,
			CLAIM_NUMBER: parsedParameters.CLAIM_NUMBER ?? null,
			JUNKING_NUMBER: parsedParameters.JUNKING_NUMBER ?? null,
			FILE_NUMBER: parsedParameters.FILE_NUMBER ?? null,
			EXTERNAL_COLOR: parsedParameters.EXTERNAL_COLOR ?? null,
			DESCRIPTION: parsedParameters.DESCRIPTION ?? null,
			PURCHASED_FROM: parsedParameters.PURCHASED_FROM ?? null,
			SELLER_CONTACT: parsedParameters.SELLER_CONTACT ?? null,
			SELLER_INFO: parsedParameters.SELLER_INFO ?? null,
		}
	}

	async function toggleEditMode(forceExit = false) {
		inEditMode = forceExit ? false : !inEditMode

		if (!inEditMode) {
			const searchFieldsSet = new Set(searchFields.map(field => field.key))
			await saveUserSetting.mutate({
				input: {
					searchVehicles: {
						//Just in case, filter out any invalid keys
						pinnedSearchFields: Array.from(pinnedSearchFields).filter(field => searchFieldsSet.has(field)),
					},
				},
			})
		}
	}

	async function loadAndSetInventoryQuestions({
		inventoryTypeId,
		manufacturerId,
		modelId,
		category,
	}: {
		inventoryTypeId?: number
		manufacturerId?: number
		modelId?: number
		category?: string | null
	}) {
		const loadedInventoryQuestions = await loadInventoryQuestions({
			inventoryTypeId,
			manufacturerId,
			modelId,
			category: category,
		})
		let inventoryQuestionNames = new Set()
		inventoryQuestions = loadedInventoryQuestions.reduce((acc: Array<SearchField>, question) => {
			//Maybe the API should be ensuring that we don't get multiple questions with the same name, but as of writing this comment(2024-04-10), it doesn't.
			if (inventoryQuestionNames.has(question.name)) {
				return acc
			} else {
				inventoryQuestionNames.add(question.name)
				return [
					...acc,
					{
						key: `QA_${question.name}`, //we use name as the key because id is specific to a type/man/model/category combo, but we want to be able to use the same question across different combinations
						name: question.name,
						dependencies: [],
						qaFieldData: question,
					},
				]
			}
		}, [])
	}

	function doSearch() {
		toggleEditMode(true)
		lastVehicleSearch.current = searchParameterValues
		if (!staticSearchFieldValues.STORE.length) {
			mediator.publish('showMessage', {
				heading: translate('vehicleSearch.noStoreSelected', 'No Store Selected'),
				message: translate(
					'vehicleSearch.pleaseSelectAStoreToSearchVehicles',
					'Please select a store to search vehicles',
				),
				color: 'danger',
				time: false,
			})
			return
		}
		logActivity()
		asr.go('app.vehicle-search.results', searchParameterValues)
	}

	function logActivity() {
		const activity: ActivityInput = {
			icon: 'truck',
			title: translate('vehicleSearch.mostRecentSearch', 'Most Recent Search'),
			type: 'VEHICLE',
			route: 'app.vehicle-search.results',
			name: 'Most Recent Search',
			// @ts-expect-error
			parameters: searchParameterValues,
		}
		if (hasSearched) {
			mediator.publish('replaceActivity', activity)
		} else {
			mediator.publish('activity', activity)
			hasSearched = true
		}
	}

	/** List of selected search fields */
	let selectedSearchFieldsKeys = $derived(
		getInitialSelectedSearchFields(staticSearchFields, userPinnedSearchFields, parsedParameters),
	)
	let inventoryQuestionValues: Record<string, InventoryQuestionValue> = $derived(getParsedQAFields(parsedParameters))
	const typeFields: Array<SearchField> = $derived(getValidTypeFields(staticSearchFieldValues.INVENTORY_TYPE))
	/** List of all search fields. */
	const searchFields: Array<SearchField> = $derived([...staticSearchFields, ...typeFields, ...inventoryQuestions])
	const searchFieldsByCategory = $derived(getSearchFieldsByCategory(staticSearchFields, typeFields, inventoryQuestions))
	/** List of search field values that will be passed to Result state */
	const searchParameterValues: Record<string, unknown> = $derived(
		computeSearchParameterValues(
			selectedSearchFieldsKeys,
			staticSearchFieldValues,
			{},
			typeFieldValues,
			inventoryQuestionValues,
			staticSearchFieldsMap,
		),
	)

	$effect(() => {
		loadAndSetInventoryQuestions({
			inventoryTypeId: staticSearchFieldValues.INVENTORY_TYPE?.id,
			manufacturerId: staticSearchFieldValues.VEHICLE_MANUFACTURER?.id,
			modelId: staticSearchFieldValues.VEHICLE_MODEL?.id,
			category: staticSearchFieldValues.CATEGORY?.name,
		})
	})

	onMount(() => {
		userPinnedSearchFields.forEach(field => pinnedSearchFields.add(field))
		typeFieldValues = getParsedTypeFields(parsedParameters)
	})
</script>

<div class="card">
	<div class="card-header">
		<div class="d-flex justify-content-between align-items-end">
			<div class="h4 mb-0">
				<div>
					<Icon
						icon="truck"
						class="me-2"
					/><span class="d-none d-sm-inline-block">{translate('common:vehicleSearch', 'Vehicle Search')}</span>
				</div>
			</div>
			<div>
				<!-- If they're not looking at results, allow them to restore their previous search with a single button click -->
				{#if asr.getActiveState().name === 'app.vehicle-search' && lastVehicleSearch.current}
					<Button
						outline
						size="sm"
						icon="history"
						class="me-2"
						href={asr.makePath('app.vehicle-search.results', lastVehicleSearch.current)}
					>
						{translate('vehicleSearch.restoreLastSearch', 'Restore Last Search')}
					</Button>
				{/if}
				<Button
					outline
					id="newVehicle"
					size="sm"
					color="success"
					iconClass="plus"
					href={asr.makePath('app.vehicle')}
				>
					{translate('vehicleSearch.newVehicle', 'New Vehicle')}
				</Button>
			</div>
		</div>
	</div>
	<div class="card-body">
		<form
			id="search-field-form"
			onsubmit={event => {
				event.preventDefault()
				doSearch()
			}}
		>
			{#if selectedSearchFieldsKeys.size}
				<div class="row row-cols-2 align-items-end">
					{#each selectedSearchFieldsKeys as searchFieldKey (searchFieldKey)}
						{@const searchField = searchFields.find(field => field.key === searchFieldKey)}
						{#if searchField}
							{#if searchField?.key === 'STORE'}
								<StoreSelect
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									id={searchField?.key}
									initialValue={parsedParameters.STORE}
									stores={authorizedStores}
									bind:selectedStores={staticSearchFieldValues.STORE}
								/>
							{:else if searchField?.key === 'INVENTORY_TYPE'}
								<InventoryTypeAutocomplete
									vehicle
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									id={searchField?.key}
									initialValue={parsedParameters.INVENTORY_TYPE
										? parseInt(parsedParameters.INVENTORY_TYPE, 10)
										: undefined}
									bind:inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
								/>
							{:else if searchField?.key === 'STATUS'}
								<StatusSelect
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									id={searchField?.key}
									bind:status={staticSearchFieldValues.STATUS}
								/>
							{:else if searchField?.key === 'USER_STATUS'}
								<UserStatusAutocomplete
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									id={searchField?.key}
									initialValue={parsedParameters.USER_STATUS}
									bind:userStatus={staticSearchFieldValues.USER_STATUS}
								/>
							{:else if searchField?.key === 'CATEGORY'}
								<CategoryAutocomplete
									vehicle
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									id={searchField?.key}
									initialValue={parsedParameters.CATEGORY}
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									bind:category={staticSearchFieldValues.CATEGORY}
								/>
							{:else if searchField?.key === 'YEAR_RANGE'}
								<YearRangeInput
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									label="Year"
									id={searchField?.key}
									bind:value={staticSearchFieldValues.YEAR_RANGE}
								/>
							{:else if searchField?.key === 'VEHICLE_MANUFACTURER'}
								<ManufacturerAutocomplete
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									type="VEHICLE"
									id={searchField?.key}
									initialValue={parsedParameters.VEHICLE_MANUFACTURER
										? parseInt(parsedParameters.VEHICLE_MANUFACTURER, 10)
										: undefined}
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									bind:manufacturer={staticSearchFieldValues.VEHICLE_MANUFACTURER}
								/>
							{:else if searchField?.key === 'VEHICLE_MODEL'}
								<ModelAutocomplete
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									type="VEHICLE"
									id={searchField?.key}
									initialValue={parsedParameters.VEHICLE_MODEL
										? parseInt(parsedParameters.VEHICLE_MODEL, 10)
										: undefined}
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									manufacturer={staticSearchFieldValues.VEHICLE_MANUFACTURER}
									bind:model={staticSearchFieldValues.VEHICLE_MODEL}
								/>
							{:else if searchField.key.startsWith('QA_')}
								<InventoryQuestionField
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									id={searchField?.key}
									value={inventoryQuestionValues[searchField.key]}
									change={ctx => (inventoryQuestionValues[searchField.key] = ctx.value)}
								/>
							{:else if searchField?.key === 'ENTERED_BY_USER'}
								<UserAccountsAutocomplete
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									label={translate('vehicleSearch.enteredByUser', 'Entered By User')}
									initialValue={parsedParameters.ENTERED_BY_USER}
									bind:userAccount={staticSearchFieldValues.ENTERED_BY_USER}
								/>
							{:else if searchField?.key === 'DISMANTLER'}
								<UserAccountsAutocomplete
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									label={translate('common:dismantler', 'Dismantler')}
									initialValue={parsedParameters.DISMANTLER}
									bind:userAccount={staticSearchFieldValues.DISMANTLER}
								/>
							{:else if searchField?.key === 'DATE_ENTERED'}
								<EditModeControls
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
								>
									<DateRange
										id={searchField?.key}
										label={translate('customer.dateEntered', 'Date Entered')}
										dates={staticSearchFieldValues.DATE_ENTERED}
										isPinned={pinnedSearchFields.has(searchField.key)}
										change={ctx => {
											staticSearchFieldValues.DATE_ENTERED = ctx.dates
										}}
									/>
								</EditModeControls>
							{:else if searchField?.key === 'TITLE_TYPE'}
								<TitleTypeAutocomplete
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									id={searchField?.key}
									bind:titleType={staticSearchFieldValues.TITLE_TYPE}
								/>
							{:else if searchField?.key === 'TITLE_STATUS'}
								<TitleStatusAutocomplete
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									id={searchField?.key}
									bind:titleStatus={staticSearchFieldValues.TITLE_STATUS}
								/>
							{:else if searchField?.key === 'LOCATION'}
								<LocationAutocomplete
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									id={searchField?.key}
									stores={staticSearchFieldValues.STORE}
									initialValue={parsedParameters.LOCATION}
									bind:inventoryLocation={staticSearchFieldValues.LOCATION}
								/>
							{:else if searchField?.key === 'DISMANTLED'}
								<DismantledCheckbox
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									bind:checked={staticSearchFieldValues.DISMANTLED}
								/>
							{:else if searchField?.key === 'RETAIL_PRICE'}
								<EditModeControls
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
								>
									<CurrencyRange
										label={translate('common:retailPrice', 'Retail Price')}
										id={searchField?.key}
										from={staticSearchFieldValues[searchField.key]?.from}
										to={staticSearchFieldValues[searchField.key]?.to}
										change={range => {
											staticSearchFieldValues[searchField.key].from = range.from
											staticSearchFieldValues[searchField.key].to = range.to
										}}
									/>
								</EditModeControls>
							{:else if searchField.key.startsWith('TYPE_FIELD_')}
								<InputSearchField
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									id={searchField?.key}
									value={typeFieldValues[searchField.key]}
									change={val => {
										typeFieldValues[searchField.key] = val.value
									}}
								/>
							{:else if vehicleStaticSearchFields.includes(searchField.key)}
								<InputSearchField
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									id={searchField?.key}
									value={staticSearchFieldValues[searchField.key]}
									change={val => {
										staticSearchFieldValues[searchField.key] = val.value
									}}
								/>
							{:else if searchField.key === 'HAS_PUBLIC_IMAGES'}
								<EditModeControls
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
								>
									<NullableRadioCheckbox
										id={searchField.key}
										label={searchField.name}
										bind:value={staticSearchFieldValues[searchField.key]}
									>
										{#snippet hint()}
											<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
										{/snippet}
									</NullableRadioCheckbox>
								</EditModeControls>
							{:else}
								<p>{translate('vehicleSearch.unsupportedFieldType', 'Unsupported field type')}: {searchField.key}</p>
							{/if}
						{/if}
					{/each}
				</div>
			{:else}
				<div class="p-5 bg-body-secondary rounded-3 my-3">
					<h1 class="display-4">{translate('vehicleSearch.noSelectedSearchFilters', 'No selected search filters.')}</h1>
					<p class="lead">
						{translate(
							'vehicleSearch.useTheAddSearchFiltersDropdownBelow',
							'Use the "Add Search Filters" dropdown below.',
						)}
					</p>
				</div>
			{/if}
		</form>
	</div>

	<div class="card-footer border-bottom">
		<AddEditSearchFilters
			{searchFieldsByCategory}
			{searchFields}
			{inEditMode}
			{pinnedSearchFields}
			{selectedSearchFieldsKeys}
			{toggleEditMode}
			label={translate('common:vehicles', 'Vehicles')}
			clear={() => {
				// Clear Results / URL
				asr.go('app.vehicle-search', {}, { inherit: false })
				// Clear Values
				staticSearchFieldValues = klona(getDefaultStaticSearchFieldValues())
				typeFieldValues = {}
				inventoryQuestionValues = {}
				// DO NOT REMOVE unpinned search fields
			}}
		/>
	</div>
</div>

<uiView></uiView>
